import { useState } from 'react';
import { Alert, Button, Card, Input, Space, Typography } from 'antd';
import { errorMessage, space, useConfirmAction } from '@viq-admin/ui';
import type { CrmConnectionRow } from './types';

/** Copy for the Console's Salesforce settings panel (spec 2026-09-28 §4.5). */
export const SF_SETTINGS_COPY = {
  title: 'Salesforce settings',
  intro:
    'Staff-only settings for this company’s Salesforce pushes. Leave empty for the standard behaviour. Paste the block from the company’s field-mapping doc, save, then run Test Connection.',
  textLabel: 'Salesforce settings (JSON)',
  placeholder: '{ "leadSource": "VendoIQ", … }',
  save: 'Save settings',
  remove: 'Remove settings',
  saved: 'Saved. Run Test Connection.',
  notJson: 'That isn’t valid JSON. Check for a missing comma, quote or brace.',
  notObject: 'The settings must be one JSON object: { … }.',
  empty: 'Paste a settings block, or use Remove settings to clear the stored one.',
  saveFailed: 'Couldn’t save the settings.',
  removeTitle: 'Remove the Salesforce settings?',
  removeContent:
    'This company’s Salesforce pushes go back to the standard behaviour: Campaigns, Lead Source “Trade Show”, and Tasks on the integration user.',
  removed: 'Settings removed.',
} as const;

export interface SalesforceSettingsPanelProps {
  connection: CrmConnectionRow;
  /** Console-injected (closure-only): PUTs `{ profile }`; `null` removes the block. */
  save: (profile: Record<string, unknown> | null) => Promise<CrmConnectionRow>;
}

function storedText(connection: CrmConnectionRow): string {
  const profile = (connection.config as { profile?: unknown }).profile;
  return profile === undefined || profile === null ? '' : JSON.stringify(profile, null, 2);
}

/** A 422's per-path issues (the console fetch exposes `details.fieldErrors` as `errors`), else the error's message. */
function issuesFrom(err: unknown): string[] {
  const fields = (err as { errors?: unknown } | null | undefined)?.errors;
  if (fields && typeof fields === 'object') {
    const lines = Object.values(fields as Record<string, unknown>)
      .flatMap((v) => (Array.isArray(v) ? v : []))
      .filter((v): v is string => typeof v === 'string' && v !== '');
    if (lines.length > 0) return lines;
  }
  return [errorMessage(err, SF_SETTINGS_COPY.saveFailed)];
}

/**
 * The Console's Salesforce settings panel (spec 2026-09-28 §4.5): a JSON box
 * pre-filled with the stored block, Save and Remove. The browser only checks
 * that the text is one JSON object; the server's schema has the final say,
 * and its 422 issues are listed under the box with their paths. Rendered
 * through `ConnectionDetail`'s `salesforceSettingsView` slot, which only the
 * Console fills.
 */
export function SalesforceSettingsPanel({ connection, save }: SalesforceSettingsPanelProps) {
  const [text, setText] = useState(() => storedText(connection));
  const [issues, setIssues] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState(false);
  const confirm = useConfirmAction();
  const hasStored = storedText(connection) !== '';

  async function submit(profile: Record<string, unknown>): Promise<void> {
    setSaving(true);
    setIssues([]);
    setSavedNote(false);
    try {
      await save(profile);
      setSavedNote(true);
    } catch (err) {
      setIssues(issuesFrom(err));
    } finally {
      setSaving(false);
    }
  }

  function handleSave() {
    setSavedNote(false);
    if (text.trim() === '') {
      setIssues([SF_SETTINGS_COPY.empty]);
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      setIssues([SF_SETTINGS_COPY.notJson]);
      return;
    }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setIssues([SF_SETTINGS_COPY.notObject]);
      return;
    }
    void submit(parsed as Record<string, unknown>);
  }

  function handleRemove() {
    confirm({
      title: SF_SETTINGS_COPY.removeTitle,
      content: SF_SETTINGS_COPY.removeContent,
      okText: SF_SETTINGS_COPY.remove,
      danger: true,
      successMessage: SF_SETTINGS_COPY.removed,
      onConfirm: async () => {
        await save(null);
        setText('');
        setIssues([]);
        setSavedNote(false);
      },
    });
  }

  return (
    <Card size="small" title={SF_SETTINGS_COPY.title} data-testid="salesforce-settings-panel">
      <Space direction="vertical" size={space.sm} style={{ width: '100%' }}>
        <Typography.Text type="secondary">{SF_SETTINGS_COPY.intro}</Typography.Text>
        <Input.TextArea
          aria-label={SF_SETTINGS_COPY.textLabel}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setSavedNote(false);
          }}
          autoSize={{ minRows: 8, maxRows: 30 }}
          placeholder={SF_SETTINGS_COPY.placeholder}
          spellCheck={false}
          style={{ fontFamily: 'monospace' }}
        />
        {issues.length > 0 && (
          <Alert
            type="error"
            showIcon
            data-testid="salesforce-settings-issues"
            message={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            }
          />
        )}
        {savedNote && <Alert type="success" showIcon message={SF_SETTINGS_COPY.saved} />}
        <Space>
          <Button type="primary" loading={saving} onClick={handleSave}>
            {SF_SETTINGS_COPY.save}
          </Button>
          <Button danger disabled={!hasStored || saving} onClick={handleRemove}>
            {SF_SETTINGS_COPY.remove}
          </Button>
        </Space>
      </Space>
    </Card>
  );
}
